import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_URL } from './config';
import DashboardComponent from './DashboardComponent';
import { Form, Col, Spinner } from 'react-bootstrap';

const SoftworksAdminReports = () => {
    const navigate = useNavigate();
    const [credentials, setCredentials] = useState({
        companyId: localStorage.getItem('company_id'),
        userId: localStorage.getItem('user_id'),
        branchId: localStorage.getItem('branch_id'),
        username: localStorage.getItem('userName')
    });
    const [states, setStates] = useState({
        totalSales: [],
        loading: true,
        error: null,
        salesData: [],
        companyDetails: {},
        branchDetails: {},
        inventoryMovement: [],
        showInventory: false,
        showPayments: false,
        comment: '',
        selectedShift: '',
        shifts: []
    });

    // Check for required credentials
    useEffect(() => {
        if (!credentials.companyId || !credentials.branchId) {
            Swal.fire({
                title: 'Error',
                text: 'Required credentials not found. Please login again.',
                icon: 'error',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate('/login');
            });
        }
    }, [credentials, navigate]);

    // Fetch initial data
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!credentials.companyId || !credentials.branchId) return;

            setStates(prev => ({ ...prev, loading: true }));
            try {
                const [companyResponse, branchResponse, shiftsResponse] = await Promise.all([
                    fetch(`${API_URL}/companysetup/${credentials.companyId}`),
                    fetch(`${API_URL}/branch/${credentials.branchId}`),
                    fetch(`${API_URL}/shift/shiftbycompanybranch/${credentials.companyId}/${credentials.branchId}`)
                ]);

                if (!companyResponse.ok || !branchResponse.ok || !shiftsResponse.ok) {
                    throw new Error('Failed to fetch initial data');
                }

                const [companyData, branchData, shiftsData] = await Promise.all([
                    companyResponse.json(),
                    branchResponse.json(),
                    shiftsResponse.json()
                ]);

                console.log("Shifts Data:", shiftsData); // Log shifts data

                setStates(prev => ({
                    ...prev,
                    companyDetails: companyData,
                    branchDetails: branchData,
                    shifts: Array.isArray(shiftsData) ? shiftsData : [shiftsData],
                    loading: false
                }));

            } catch (error) {
                console.error('Error fetching initial data:', error);
                setStates(prev => ({
                    ...prev,
                    error: error.message,
                    loading: false
                }));

                Swal.fire({
                    title: 'Error',
                    text: `Failed to fetch data: ${error.message}`,
                    icon: 'error',
                    confirmButtonText: 'Retry'
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.reload();
                    }
                });
            }
        };

        fetchInitialData();
    }, [credentials.companyId, credentials.branchId]);

    // Handle shift change with better error handling
    const handleShiftChange = async (e) => {
        const shiftId = e.target.value;
        if (!shiftId) return;

        setStates(prev => ({ ...prev, loading: true, selectedShift: shiftId }));

        try {
            const [salesResponse, inventoryResponse, totalSalesResponse] = await Promise.all([
                fetch(`${API_URL}/salepostrans/get_sale_pos_trans_sold_by_shift/${credentials.branchId}/${credentials.companyId}/${shiftId}`),
                fetch(`${API_URL}/inventorymgt/inventory_movement/${credentials.branchId}/${credentials.companyId}/${shiftId}`),
                fetch(`${API_URL}/salepostrans/total/sales/${credentials.branchId}/${credentials.companyId}/${shiftId}`)
            ]);

            if (!salesResponse.ok || !inventoryResponse.ok || !totalSalesResponse.ok) {
                throw new Error('Failed to fetch shift data');
            }

            const [salesData, inventoryData, totalSalesData] = await Promise.all([
                salesResponse.json(),
                inventoryResponse.json(),
                totalSalesResponse.json()
            ]);

            setStates(prev => ({
                ...prev,
                salesData: Array.isArray(salesData) ? salesData : [salesData],
                inventoryMovement: Array.isArray(inventoryData) ? inventoryData : [inventoryData],
                totalSales: Array.isArray(totalSalesData) ? totalSalesData : [totalSalesData],
                loading: false
            }));

        } catch (error) {
            console.error('Error fetching shift data:', error);
            setStates(prev => ({
                ...prev,
                error: error.message,
                loading: false
            }));

            Swal.fire({
                title: 'Error',
                text: `Failed to fetch shift data: ${error.message}`,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    // Handle shift closure
    const handleCloseShift = async () => {
        if (!states.comment) {
            Swal.fire('Warning', 'Please enter a comment before closing the shift.', 'warning');
            return;
        }

        try {
            const shiftResponse = await fetch(`${API_URL}/shift/${states.selectedShift}`);
            if (!shiftResponse.ok) throw new Error('Failed to fetch shift details');
            
            const shiftData = await shiftResponse.json();
            
            const updateResponse = await fetch(`${API_URL}/shift/${states.selectedShift}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...shiftData,
                    company_id: credentials.companyId,
                    branch_id: credentials.branchId,
                    shift_status: "closed",
                    date_time_close: new Date().toISOString(),
                    shift_comment: states.comment,
                    operator_id: credentials.userId
                })
            });

            if (!updateResponse.ok) throw new Error('Failed to close shift');

            Swal.fire({
                title: 'Success',
                text: 'Shift closed successfully',
                icon: 'success',
                confirmButtonText: 'OK'
            }).then(() => {
                localStorage.clear();
                navigate('/');
            });

        } catch (error) {
            console.error('Error closing shift:', error);
            Swal.fire('Error', `Failed to close shift: ${error.message}`, 'error');
        }
    };

    if (states.loading) {
        return (
            <DashboardComponent>
                <div className="d-flex justify-content-center align-items-center min-vh-100">
                    <Spinner animation="border" variant="primary" />
                </div>
            </DashboardComponent>
        );
    }

    return (
        <DashboardComponent>
            <div className="container-fluid py-4" style={{ backgroundColor: 'white' }}>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Shift Selection</Form.Label>
                        <Form.Select
                            name="shift_selection"
                            value={states.selectedShift}
                            onChange={handleShiftChange}
                        >
                            <option value="">Select a Shift</option>
                            {states.shifts.length > 0 ? (
                                states.shifts.map((shift) => (
                                    <option key={shift.id} value={shift.id}>{shift.name}</option>
                                ))
                            ) : (
                                <option disabled>No shifts available</option>
                            )}
                        </Form.Select>
                    </Form.Group>
                </Col>

                {/* Display company information */}
                <div className="card col-md-12 mb-4">
                    <div className="card-header bg-secondary text-white">
                        <h5 className="mb-0">Company Information</h5>
                    </div>
                    <div className="card-body">
                        <div className="col-md-12 text-start">
                            <p className="mb-1"><strong>Name:</strong> {states.companyDetails.name}</p>
                            <p className="mb-1"><strong>Email:</strong> {states.companyDetails.email1}</p>
                            <p className="mb-1"><strong>Phone:</strong> {states.companyDetails.phone_number1}</p>
                        </div>
                    </div>
                </div>

                {/* Main content displays after selecting a shift */}
                {states.selectedShift && (
                    <>
                        {/* Cash Information */}
                        <div className="d-flex justify-content-end">
                            <div className="card col-md-12 mb-4">
                                <div className="card-header bg-white">
                                    <h5 className="mb-0 text-end">Cash Information</h5>
                                </div>
                                <div className="card-body">
                                    <table className="table">
                                        <thead>
                                            <tr>
                                                <th>Total Debit</th>
                                                <th>Total Credit</th>
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {states.totalSales.map((sale, index) => (
                                                <tr key={index}>
                                                    <td className="text-end">
                                                        {sale.total_amntrec?.toFixed(2)} {sale.currency}
                                                    </td>
                                                    <td className="text-end">
                                                        {sale.total_amntrec_credit?.toFixed(2)} {sale.currency}
                                                    </td>
                                                    <td className="text-end">
                                                        {(sale.total_amntrec + sale.total_amntrec_credit)?.toFixed(2)} {sale.currency}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* Inventory Movement */}
                        <div className="card mb-4">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center" 
                                 onClick={() => setStates(prev => ({ ...prev, showInventory: !prev.showInventory }))} 
                                 style={{ cursor: 'pointer' }}>
                                <h5 className="mb-0">Inventory Movement</h5>
                                <span>{states.showInventory ? '▼' : '▶'}</span>
                            </div>
                            {states.showInventory && (
                                <div className="card-body">
                                    {states.inventoryMovement.length > 0 ? (
                                        <div className="table-responsive">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Product ID</th>
                                                        <th>Code</th>
                                                        <th>Name</th>
                                                        <th>Description</th>
                                                        <th>Quantity</th>
                                                        <th>Cost</th>
                                                        <th>Selling Price</th>
                                                        <th>Gross Profit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {states.inventoryMovement.map((item, index) => {
                                                        const grossProfit = (item.selling_price - item.cost) * item.quantity;
                                                        return (
                                                            <tr key={index}>
                                                                <td>{item.product_id}</td>
                                                                <td>{item.product_code}</td>
                                                                <td>{item.product_name}</td>
                                                                <td>{item.description}</td>
                                                                <td>{item.quantity}</td>
                                                                <td>{item.total_unit_cost}</td>
                                                                <td>{item.total_selling_price}</td>
                                                                <td>{grossProfit.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="alert alert-info">No inventory movement data available</div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Payments Information */}
                        <div className="card mb-4">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center"
                                 onClick={() => setStates(prev => ({ ...prev, showPayments: !prev.showPayments }))}
                                 style={{ cursor: 'pointer' }}>
                                <h5 className="mb-0">Payments Information</h5>
                                <span>{states.showPayments ? '▼' : '▶'}</span>
                            </div>
                            {states.showPayments && (
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead>
                                                <tr>
                                                    <th>Product ID</th>
                                                    <th>Code</th>
                                                    <th>Name</th>
                                                    <th>Description</th>
                                                    <th>Quantity</th>
                                                    <th>Amount</th>
                                                    <th>Currency</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {states.salesData.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.product_id}</td>
                                                        <td>{item.product_code}</td>
                                                        <td>{item.product_name}</td>
                                                        <td>{item.description}</td>
                                                        <td>{item.quantity}</td>
                                                        <td>{item.amntrec}</td>
                                                        <td>{item.currency}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Report Comments */}
                        <div className="card mb-4">
                            <div className="card-header bg-white">
                                <h5 className="mb-0">Report Comments</h5>
                            </div>
                            <div className="card-body">
                                <textarea 
                                    className="form-control mb-3" 
                                    rows="3" 
                                    placeholder="Enter report comments..."
                                    value={states.comment}
                                    onChange={(e) => setStates(prev => ({ ...prev, comment: e.target.value }))}
                                ></textarea>
                                <div className="text-end">
                                    <button 
                                        className="btn btn-secondary" 
                                        onClick={handleCloseShift}
                                    >
                                        Close Shift
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </DashboardComponent>
    );
};

export default SoftworksAdminReports;